import { ref, get, set, update } from 'firebase/database';
import { database } from '../services/firebase';

/**
 * Database Migration Script for Multi-Language Support
 *
 * This script migrates existing single-language content to multi-language format
 * by copying English content to all 4 languages (nl, fr, de, en)
 */

const LANGUAGES = ['nl', 'fr', 'de', 'en'];

export const migrationScript = {
  /**
   * Run full migration
   * @returns {Object} Migration results
   */
  async runFullMigration() {
    console.log('🚀 Starting multi-language migration...');
    const results = {
      success: [],
      errors: [],
      backup: null
    };

    try {
      // Step 1: Create backup
      console.log('📦 Creating backup...');
      results.backup = await this.createBackup();
      console.log('✅ Backup created successfully');

      // Step 2: Migrate company info
      console.log('🏢 Migrating company info...');
      await this.migrateCompanyInfo();
      results.success.push('company');
      console.log('✅ Company info migrated');

      // Step 3: Migrate about page
      console.log('📝 Migrating about page...');
      await this.migrateAboutPage();
      results.success.push('aboutPage');
      console.log('✅ About page migrated');

      // Step 4: Migrate team members
      console.log('👥 Migrating team members...');
      await this.migrateTeamMembers();
      results.success.push('teamMembers');
      console.log('✅ Team members migrated');

      // Step 5: Migrate gallery items
      console.log('🖼️  Migrating gallery items...');
      await this.migrateGalleryItems();
      results.success.push('galleryItems');
      console.log('✅ Gallery items migrated');

      // Step 6: Migrate categories
      console.log('📂 Migrating categories...');
      await this.migrateCategories();
      results.success.push('categories');
      console.log('✅ Categories migrated');

      // Step 7: Migrate privacy policy
      console.log('🔒 Migrating privacy policy...');
      await this.migratePrivacyPolicy();
      results.success.push('privacyPolicy');
      console.log('✅ Privacy policy migrated');

      // Step 8: Migrate terms & conditions
      console.log('📜 Migrating terms & conditions...');
      await this.migrateTerms();
      results.success.push('terms');
      console.log('✅ Terms & conditions migrated');

      console.log('🎉 Migration completed successfully!');
      return { success: true, results };

    } catch (error) {
      console.error('❌ Migration failed:', error);
      results.errors.push(error.message);
      return { success: false, results, error };
    }
  },

  /**
   * Create backup of current database
   */
  async createBackup() {
    const websiteRef = ref(database, 'website');
    const snapshot = await get(websiteRef);

    if (snapshot.exists()) {
      const data = snapshot.val();
      const timestamp = Date.now();

      // Save backup to a backup node
      const backupRef = ref(database, `backups/migration_${timestamp}`);
      await set(backupRef, {
        data,
        timestamp,
        type: 'pre_migration'
      });

      return { timestamp, success: true };
    }

    return { success: false };
  },

  /**
   * Migrate company info
   */
  async migrateCompanyInfo() {
    const companyRef = ref(database, 'website/company');
    const snapshot = await get(companyRef);

    if (!snapshot.exists()) {
      console.log('No company data to migrate');
      return;
    }

    const data = snapshot.val();
    const updates = {};

    // Migrate address (translatable)
    if (data.address) {
      LANGUAGES.forEach(lang => {
        updates[`address_${lang}`] = data.address;
      });
      updates.address = null; // Remove old field
    }

    // Keep non-translatable fields as-is
    // (name, email, phone, socialMedia)

    if (Object.keys(updates).length > 0) {
      await update(companyRef, updates);
    }
  },

  /**
   * Migrate about page content
   */
  async migrateAboutPage() {
    const aboutRef = ref(database, 'website/aboutPage');
    const snapshot = await get(aboutRef);

    if (!snapshot.exists()) {
      console.log('No about page data to migrate');
      return;
    }

    const data = snapshot.val();
    const updates = {};

    // Migrate translatable fields
    const fields = ['companyStory', 'mission', 'vision'];

    fields.forEach(field => {
      if (data[field]) {
        LANGUAGES.forEach(lang => {
          updates[`${field}_${lang}`] = data[field];
        });
        updates[field] = null; // Remove old field
      }
    });

    if (Object.keys(updates).length > 0) {
      await update(aboutRef, updates);
    }
  },

  /**
   * Migrate team members
   */
  async migrateTeamMembers() {
    const membersRef = ref(database, 'website/aboutPage/teamMembers');
    const snapshot = await get(membersRef);

    if (!snapshot.exists()) {
      console.log('No team members to migrate');
      return;
    }

    const members = snapshot.val();

    for (const [memberId, memberData] of Object.entries(members)) {
      const updates = {};

      // Migrate role and bio
      if (memberData.role) {
        LANGUAGES.forEach(lang => {
          updates[`role_${lang}`] = memberData.role;
        });
        updates.role = null;
      }

      if (memberData.bio) {
        LANGUAGES.forEach(lang => {
          updates[`bio_${lang}`] = memberData.bio;
        });
        updates.bio = null;
      }

      if (Object.keys(updates).length > 0) {
        const memberRef = ref(database, `website/aboutPage/teamMembers/${memberId}`);
        await update(memberRef, updates);
      }
    }
  },

  /**
   * Migrate gallery items
   */
  async migrateGalleryItems() {
    const itemsRef = ref(database, 'website/gallery/items');
    const snapshot = await get(itemsRef);

    if (!snapshot.exists()) {
      console.log('No gallery items to migrate');
      return;
    }

    const items = snapshot.val();

    for (const [itemId, itemData] of Object.entries(items)) {
      const updates = {};

      // Migrate title and description
      if (itemData.title) {
        LANGUAGES.forEach(lang => {
          updates[`title_${lang}`] = itemData.title;
        });
        updates.title = null;
      }

      if (itemData.description) {
        LANGUAGES.forEach(lang => {
          updates[`description_${lang}`] = itemData.description;
        });
        updates.description = null;
      }

      if (Object.keys(updates).length > 0) {
        const itemRef = ref(database, `website/gallery/items/${itemId}`);
        await update(itemRef, updates);
      }
    }
  },

  /**
   * Migrate categories
   */
  async migrateCategories() {
    const categoriesRef = ref(database, 'website/gallery/categories');
    const snapshot = await get(categoriesRef);

    if (!snapshot.exists()) {
      console.log('No categories to migrate');
      return;
    }

    const categories = snapshot.val();

    for (const [categoryId, categoryData] of Object.entries(categories)) {
      const updates = {};

      // Migrate name
      if (categoryData.name) {
        LANGUAGES.forEach(lang => {
          updates[`name_${lang}`] = categoryData.name;
        });
        updates.name = null;
      }

      if (Object.keys(updates).length > 0) {
        const categoryRef = ref(database, `website/gallery/categories/${categoryId}`);
        await update(categoryRef, updates);
      }
    }
  },

  /**
   * Migrate privacy policy
   */
  async migratePrivacyPolicy() {
    const privacyRef = ref(database, 'website/pages/privacyPolicy');
    const snapshot = await get(privacyRef);

    if (!snapshot.exists()) {
      console.log('No privacy policy to migrate');
      return;
    }

    const data = snapshot.val();
    const updates = {};

    if (data.content) {
      LANGUAGES.forEach(lang => {
        updates[`content_${lang}`] = data.content;
      });
      updates.content = null;
    }

    if (Object.keys(updates).length > 0) {
      await update(privacyRef, updates);
    }
  },

  /**
   * Migrate terms & conditions
   */
  async migrateTerms() {
    const termsRef = ref(database, 'website/pages/termsAndConditions');
    const snapshot = await get(termsRef);

    if (!snapshot.exists()) {
      console.log('No terms to migrate');
      return;
    }

    const data = snapshot.val();
    const updates = {};

    if (data.content) {
      LANGUAGES.forEach(lang => {
        updates[`content_${lang}`] = data.content;
      });
      updates.content = null;
    }

    if (Object.keys(updates).length > 0) {
      await update(termsRef, updates);
    }
  },

  /**
   * Rollback to backup
   * @param {number} timestamp - Backup timestamp
   */
  async rollback(timestamp) {
    console.log(`🔄 Rolling back to backup ${timestamp}...`);

    const backupRef = ref(database, `backups/migration_${timestamp}`);
    const snapshot = await get(backupRef);

    if (!snapshot.exists()) {
      throw new Error('Backup not found');
    }

    const backupData = snapshot.val().data;
    const websiteRef = ref(database, 'website');

    await set(websiteRef, backupData);
    console.log('✅ Rollback completed successfully');

    return { success: true };
  },

  /**
   * Verify migration
   */
  async verifyMigration() {
    console.log('🔍 Verifying migration...');
    const issues = [];

    // Check company info
    const companySnapshot = await get(ref(database, 'website/company'));
    if (companySnapshot.exists()) {
      const data = companySnapshot.val();
      if (data.address && !data.address_nl) {
        issues.push('Company address not migrated');
      }
    }

    // Add more verification checks as needed...

    if (issues.length === 0) {
      console.log('✅ Verification passed');
      return { success: true, issues: [] };
    } else {
      console.log('⚠️  Verification found issues:', issues);
      return { success: false, issues };
    }
  }
};

export default migrationScript;
